<template>
  <div
    id="CreateOrganization"
    class="authentication-container"
    :class="{'authentication-container-mobile': isMobileSize}"
  >

    <!-- LEFT SIDE -->

    <left-side v-if="!isMobileSize"/>

    <!-- RIGHT SIDE -->

    <div class="right-side" :class="{'mobile': isMobileSize}">

      <!-- TITLE -->

      <div class="title">
        <h3 class="title-header">Create Account</h3>
      </div>

      <!-- INPUTS -->

      <div class="content form" :class="{'mobile': isMobileSize}">
        <div class="inputs">
          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="organizationName"
              class="label"
            >Account Name*</label>
            <input
              type="text"
              id="organizationName"
              class="primary-input"
              v-model="$v.credentials.organizationName.$model"
              :class="{error: $v.credentials.organizationName.$error}"
            >
            <validation-message
              :validation="$v.credentials.organizationName"
              :metadata="organizationMetadata.fields.title"
            />
          </div>
          <div class="input-container" :class="{'mobile': isMobileSize}" v-if="isRegistered && !googleToken">
            <label
              for="email"
              class="label"
            >{{ memberMetadata.fields.email.label }}</label>
            <input
              type="text"
              id="email"
              class="primary-input"
              v-model="$v.credentials.email.$model"
              :class="{error: $v.credentials.email.$error}"
            >
            <validation-message
              :validation="$v.credentials.email"
              :metadata="memberMetadata.fields.email"
            />
          </div>

          <div class="input-container"  :class="{'mobile': isMobileSize}" v-if="!isRegistered && !googleToken">
            <label
              for="firstName"
              class="label"
            >First Name*</label>
            <input
              type="text"
              id="firstName"
              class="primary-input"
              v-model="$v.credentials.firstName.$model"
              :class="[$v.credentials.firstName.$error ? 'error' : null,
                            $v.credentials.firstName.$dirty &&
                            !$v.credentials.firstName.$invalid ? 'success' : null]"
            >
            <validation-message
              :validation="$v.credentials.firstName"
              :metadata="memberMetadata.fields.firstName"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}" v-if="!isRegistered && !googleToken">
            <label
              for="lastName"
              class="label"
            >Last Name*</label>
            <input
              type="text"
              id="lastName"
              class="primary-input"
              v-model="$v.credentials.lastName.$model"
              :class="[$v.credentials.lastName.$error ? 'error' : null,
                            $v.credentials.lastName.$dirty &&
                            !$v.credentials.lastName.$invalid ? 'success' : null]"
            >
            <validation-message
              :validation="$v.credentials.lastName"
              :metadata="memberMetadata.fields.lastName"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}" v-if="!googleToken">
            <label
              for="password"
              class="label"
            >Password*</label>
            <CoreInput
              type="password"
              required
              placeholder=""
              id="password"
              class="primary-input"
              :error="$v.credentials.password.$error"
              v-model="$v.credentials.password.$model"
              :class="[$v.credentials.password.$error ? 'error' : null,
                            $v.credentials.password.$dirty &&
                            !$v.credentials.password.$invalid ? 'success' : null]"
            />
            <password-validation-message
              :validation="$v.credentials.password"
              :metadata="memberMetadata.fields.password"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}" v-if="!isRegistered && !googleToken">
            <label
              for="confirmPassword"
              class="label"
            >Confirm Password*</label>
            <input
              type="password"
              id="confirmPassword"
              class="primary-input"
              v-model="$v.credentials.confirmPassword.$model"
              :class="[$v.credentials.confirmPassword.$error ? 'error' : null,
                            $v.credentials.confirmPassword.$dirty &&
                            !$v.credentials.confirmPassword.$invalid ? 'success' : null]"
            >
            <password-validation-message
              :validation="$v.credentials.confirmPassword"
              :confirmPassword="true"
            />
          </div>

          <!-- ACTIONS -->
        </div>
        <div class="actions">
          <CoreButton
            @click.prevent="createOrganization"
            :disabled="isDisabled"
            :loading="isLoading"
          >
            Create
          </CoreButton>

          <CoreButton @click="loginWithGoogle" class="login-google" v-if="!googleToken">
            Login with Google
            <img
              src="@/assets/google-icon.png"
              alt="alt"
              width="18px"
            >
          </CoreButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import server, { createValidator } from './../server'
import { mapMutations, mapActions } from 'vuex'
import { required, sameAs } from 'vuelidate/lib/validators'
import { mixin as clickout } from 'vue-clickout'
import { DEFAULT_REDIRECT_URL, DEFAULT_SCOPES, DEFAULT_APPLICATION_ID, GOOGLE_CLIENT_ID, GOOGLE_OAUTH_URL } from '../settings'
import { GatewayService, PandaService } from '../utils/request'
import { WaitStates } from '../utils/vuewait'
const jwtDecode = require('jwt-decode')

const CoreInput = () => import(
  /* webpackChunkName: "CoreInput" */ './../components/Core/CoreInput.vue'
)

const CoreButton = () => import(
  /* webpackChunkName: "CoreButton" */ './../components/Core/CoreButton.vue'
)
const LeftSide = () => import(
  /* webpackChunkName: "LeftSide" */ './../components/LeftSide'
)
const ValidationMessage = () => import(
  /* webpackChunkName: "ValidationMessage" */ './../components/ValidationMessage'
)
const PasswordValidationMessage = () => import(
  /* webpackChunkName: "PasswordValidationMessage" */ './../components/PasswordValidationMessage'
)

export default {
  mixins: [clickout],
  name: 'CreateOrganization',
  data () {
    return {
      organizationId: null,
      authorizationCode: null,
      token: null,
      credentials: {
        firstName: null,
        lastName: null,
        password: null,
        confirmPassword: null,
        organizationName: null,
        email: null
      },
      memberMetadata: server.metadata,
      organizationMetadata: server.organizationMetadata,
      applicationId: this.$route.query.applicationId || DEFAULT_APPLICATION_ID,
      state: this.$route.query.state,
      redirectUri: this.$route.query.redirectUri || DEFAULT_REDIRECT_URL,
      scopes: this.$route.query.scopes || DEFAULT_SCOPES.join(','),
      googleToken: this.$route.query.code,
      googleState: this.$route.query.state
    }
  },
  validations () {
    return {
      credentials: {
        email: {
          required
        },
        firstName: createValidator(this.memberMetadata.fields.firstName),
        lastName: createValidator(this.memberMetadata.fields.lastName),
        password: createValidator(this.memberMetadata.fields.password),
        organizationName: createValidator(this.organizationMetadata.fields.title),
        confirmPassword: {
          sameAs: sameAs('password')
        }
      }
    }
  },
  computed: {
    member () {
      return JSON.parse(atob(this.token.split('.')[1]))
    },
    query () {
      let queries = {}
      if (this.applicationId) {
        queries.applicationId = this.applicationId
      }
      if (this.state) {
        queries.state = this.state
      }
      if (this.redirectUri) {
        queries.redirectUri = this.redirectUri
      }
      if (this.scopes) {
        queries.scopes = this.scopes
      }
      if (this.token) {
        queries.organizationId = String(this.organizationId)
      }
      return queries
    },
    isDisabled () {
      const { firstName, lastName, confirmPassword, email, password, organizationName } = this.$v.credentials

      if (this.googleToken) {
        return organizationName.$invalid
      }
      if (!this.isRegistered) {
        return organizationName.$invalid || firstName.$invalid || lastName.$invalid || password.$invalid || confirmPassword.$invalid
      }
      return email.$invalid || password.$invalid || organizationName.$invalid
    },
    isLoading () {
      return this.$wait.is(WaitStates.CREATE_ORGANIZATION_LOADING)
    },
    isRegistered () {
      const memberData = jwtDecode(this.token)
      return JSON.parse(memberData.isRegistered.toLowerCase())
    },
    isMobileSize () {
      return window.innerWidth <= 768
    }
  },
  methods: {
    async createOrganization () {
      let form = {}

      if (this.googleToken) {
        form = {
          oauthCode: this.googleToken,
          ownershipToken: JSON.parse(this.googleState).token,
          redirectUrl: window.location.href.split('?')[0],
          organizationTitle: this.credentials.organizationName
        }
      } else {
        form = {
          email: this.credentials.email || this.member.email,
          password: this.credentials.password,
          organizationTitle: this.credentials.organizationName,
          ownershipToken: this.token
        }
        if (!this.isRegistered) {
          form.firstName = this.credentials.firstName
          form.lastName = this.credentials.lastName
          form.email = this.member.email
        }
      }

      this.$wait.start(WaitStates.CREATE_ORGANIZATION_LOADING)
      const { status, data, headers } = await GatewayService({
        url: 'organizations',
        method: 'INITIATE',
        data: JSON.stringify(form)
      })
      this.$wait.end(WaitStates.CREATE_ORGANIZATION_LOADING)
      if (status === 200) {
        this.organizationId = data.id
        this.authorizationCode = headers['x-new-jwt-token'] || ''
        if (this.authorizationCode && this.googleToken) {
          let decoded = jwtDecode(this.authorizationCode)
          server.token = this.authorizationCode
          this.state = decoded.email
          this.confirmIdentity()
          if (this.applicationId) {
            this.$router.push({ path: '/permissions', query: this.query })
          } else if (this.$route.query.origin) {
            this.$router.push(this.$route.query.origin)
          } else {
            this.$router.push('/')
          }
        } else {
          await this.createToken()
        }
      }
    },
    async createToken () {
      const form = {
        email: this.credentials.email || this.member.email,
        password: this.credentials.password
      }
      const { status, data } = await PandaService({
        url: 'tokens',
        method: 'CREATE',
        data: JSON.stringify(form)
      })
      if (status === 200) {
        server.token = data.token
        this.state = form.email
        this.confirmIdentity()
        if (this.applicationId) {
          this.$router.push({ path: '/permissions', query: this.query })
        } else if (this.$route.query.origin) {
          this.$router.push(this.$route.query.origin)
        } else {
          this.$router.push('/')
        }
      }
    },
    loginWithGoogle () {
      window.location.href = `${GOOGLE_OAUTH_URL}?response_type=code&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${window.location.protocol}//${window.location.hostname}%2Fcreate_account&scope=email+profile&state=${this.stringifyRegisterData()}`
    },
    stringifyRegisterData () {
      const registerData = {
        token: this.token,
        applicationId: this.applicationId,
        state: this.state,
        scopes: this.scopes,
        redirectUri: this.redirectUri
      }

      return JSON.stringify(registerData)
    },
    ...mapMutations([
      'confirmIdentity'
    ]),
    ...mapActions([
      'removeToken'
    ])
  },
  beforeMount () {
    if (this.googleToken) {
      const registerData = JSON.parse(this.googleState)

      this.applicationId = registerData.applicationId
      this.state = registerData.state
      this.scopes = registerData.scopes
      this.token = registerData.token
      this.redirectUri = registerData.redirectUri
    } else {
      this.removeToken()
      this.token = this.$route.query.t_
    }
  },
  components: {
    LeftSide,
    ValidationMessage,
    PasswordValidationMessage,
    CoreButton,
    CoreInput
  }
}
</script>
